import * as THREE from "three";
import Canvas from "../classes/canvas";
import * as OIMO from "oimo";
import Assets from "./assets";
import addDirectAO from "../shaders/addDirectAO";
import addColorWipe from "../shaders/addColorWipe";
import addNoiseOffset from "../shaders/addNoiseOffset";
import addSnowfall from "../shaders/addSnowfall";
import customLighting from "../shaders/customLighting";

const init = (canvas, data, assets) => {
  const materials = assets.materials;
  const scene = new THREE.Scene();
  scene.environment = assets.refl; //assets.hdrRefl.texture;
  scene.background = new THREE.Color(0x161519);
  canvas.scene = scene;

  materials.Tyres.onBeforeCompile = shader => {
    customLighting(shader);
    addDirectAO(shader);
  };

  materials.Body.userData.shader = null;
  materials.Body.onBeforeCompile = shader => {
    customLighting(shader);
    addColorWipe(shader, data.defaults.color);
    materials.Body.userData.shader = shader;
  };
  materials.Car.onBeforeCompile = shader => {
    customLighting(shader);
  };

  canvas.materials = materials;

  const snow = new THREE.Object3D();
  snow.add(createSnow(canvas, assets.snowflake1, 0.1, 0.25));
  snow.add(createSnow(canvas, assets.snowflake2, -0.2, 0.2));
  snow.add(createSnow(canvas, assets.snowflake2, 0, 0.15));
  scene.add(snow);
  snow.visible = false;
  canvas.snow = snow;

  var planeGeo = new THREE.PlaneBufferGeometry(1000, 1000);
  planeGeo.rotateX(-Math.PI / 2);
  var plane = new THREE.Mesh(planeGeo, materials.ShadowReceiver);
  plane.position.set(0, 0.02, 0);
  plane.receiveShadow = true;
  scene.add(plane);

  const shadowLight = new THREE.SpotLight(0xffffff, 0);
  shadowLight.position.set(0, 20, 0);
  shadowLight.castShadow = true;
  shadowLight.shadow.radius = 8;
  shadowLight.shadow.mapSize.width = 512;
  shadowLight.shadow.mapSize.height = 512;
  scene.add(shadowLight);

  const ambient = new THREE.AmbientLight(0xc0c0c0);
  scene.add(ambient);

  const aspect = canvas.width / canvas.height;
  const mobile = aspect < 1.6;

  canvas.camera = new THREE.PerspectiveCamera(18, aspect, 0.1, 1000);

  canvas.camera.position.set(mobile ? -0.8 : 0, 1.85, mobile ? 12.5 : 11.8);
  canvas.camera.lookAt(0, 0.35, -10);
  canvas.camera.updateMatrix();
  scene.add(canvas.camera);

  /* Some state variables */
  canvas.colorQueue = [];
  canvas.spawnHeight = 1.2;
  canvas.spawnRotation = 50;
  canvas.flipTimer = 0;
  canvas.pushTimer = 0;
  canvas.oldX = 0;
  canvas.lastInteractionTime = canvas.time.current + 8000;
  canvas.centerForce = new THREE.Vector3(0, 0, 0);
  canvas.forceOrigin = new THREE.Vector3(0, 0, 0);
  canvas.applyForce = false;

  /* Init physics world */
  canvas.world = new OIMO.World({
    timestep: 1 / 60,
    iterations: 6,
    broadphase: 2, // 1 brute force, 2 sweep and prune, 3 volume tree
    worldscale: 1,
    random: true,
    info: false,
    gravity: [0, -9.8, 0]
  });

  /* Create car and floor rigidbodies */
  canvas.body = canvas.world.add({
    type: "box",
    name: "car",
    size: [5, 1.9, 2.2],
    pos: [0, canvas.spawnHeight, 0],
    rot: [0, canvas.spawnRotation, 0],
    move: true,
    density: 0.1,
    friction: 0.6,
    restitution: 0.4,
    belongsTo: 2, // The bits of the collision groups to which the shape belongs.
    collidesWith: 0x000000001 // The bits of the collision groups with which the shape collides.
  });

  canvas.floor = canvas.world.add({
    type: "box",
    name: "floor",
    size: [30, 1, 30],
    pos: [0, -0.5, 0],
    rot: [0, 0, 0],
    move: false,
    kinematic: true,
    density: 10,
    friction: 0.25,
    restitution: 0.2,
    belongsTo: 1,
    collidesWith: 0xffffffff
  });
  /* Find car root in file */
  assets.car.scene.traverse(child => {
    if (child.name == "Car") {
      canvas.car = child;
    }
  });

  const car = canvas.car;
  /* Transform car to spawn position, to make sure wheels attach correctly */
  car.scale.set(1.2, 1.2, 1.2);
  car.position.set(0, canvas.spawnHeight, 0);
  car.rotateY(canvas.spawnRotation * (Math.PI / 180));
  car.updateWorldMatrix(false, true);
  canvas.carCameraPos = car.position.clone().project(canvas.camera);
  canvas.carUpDirection = new THREE.Vector3(0, 1, 0);
  canvas.oldX = car.position.x;

  const wheels = [];
  car.traverse(child => {
    if (child.name == "Collider") {
      canvas.collider = child;
      child.visible = false;
    } else if (child.name == "ShadowProxy") {
      /* A proxy mesh is used for cheaper shadow rendering */
      child.material = materials.ShadowCaster;
      child.castShadow = true;
    }
    if (child.isMesh) {
      if (materials[child.material.name]) {
        child.material = materials[child.material.name];
        if (child.material.onBeforeCompile === undefined) {
          child.material.onBeforeCompile = customLighting;
        }
      }
    }

    if (child.name.includes("Wheel_")) {
      wheels.push(child);
      child.localPos = child.position.clone();
    }
  });

  /* Attach springs and colliders to the wheels */
  wheels.map(wheel => {
    canvas.scene.attach(wheel);

    wheel.updateWorldMatrix(true, true);
    const localPos = wheel.localPos.toArray();
    const worldPos = wheel.getWorldPosition(new THREE.Vector3(0, 0, 0));
    /* For some reason, this fudge factor improves wheel spawning position. */
    worldPos.y += canvas.spawnHeight / 2;

    const body = canvas.world.add({
      type: "cylinder",
      move: true,
      size: [0.18, 0.8, 0.18],
      rotShape: [0, 0, 90],
      name: wheel.name,
      pos: worldPos.toArray(),
      rot: [0, canvas.spawnRotation, 0],
      density: 1,
      friction: 1.0,
      restitution: 0.1,
      belongsTo: 1,
      collidesWith: 0x000000001
    });

    body.connectMesh(wheel);
    wheel.body = body;
    wheel.spawnPos = worldPos;

    const joint = canvas.world.add({
      type: "jointPrisme",
      collision: false,
      body1: wheel.name,
      body2: "car",
      axe1: [0, 1, 0],
      axe2: [0, 1, 0],
      pos1: [0, 0, 0],
      pos2: [localPos[0] * 1.2, (localPos[1] - 0.09) * 1.2, localPos[2] * 1.2],
      min: -0.5,
      max: 0.05
    });
    joint.limitMotor.frequency = 5;
    joint.limitMotor.dampingRatio = 1.3;
    /* Hack to fix physics issues related to the angular constraint solver. */
    joint.ac = { solve: () => {}, preSolve: () => {} };
  });
  canvas.wheels = wheels;
  scene.add(car);

  setPHEV(canvas, data?.defaults?.engine === "engine-phev");
  setTowbar(canvas, data?.defaults?.towbar);
  displaySnow(canvas, data?.defaults?.snow);

  /* Apply force depending on mouse delta */
  canvas.input.on("mousemove", (x, y) => {
    if (canvas.applyForce) {
      const dt = canvas.time.delta;
      const force = new THREE.Vector3((x / (dt * canvas.height)) * 0.11, (-y / (dt * canvas.height)) * 0.11, 0);
      canvas.body.applyImpulse(canvas.forceOrigin, force);
      canvas.lastInteractionTime = canvas.time.current;
    }
  });

  /* Detect when a mouse click intersects the car */
  canvas.input.on("mousedown", (x, y) => {
    const mouse = new THREE.Vector2((x / canvas.width) * 2 - 1, -(y / canvas.height) * 2 + 1);
    canvas.camera.updateMatrixWorld();
    canvas.raycaster.setFromCamera(mouse, canvas.camera);
    var intersects = canvas.raycaster.intersectObject(canvas.collider);
    if (intersects.length > 0) {
      canvas.forceOrigin = intersects[0].point;
      canvas.applyForce = true;
    } else {
      canvas.applyForce = false;
    }
  });

  /* Connect the car mesh to the rigidbody */
  canvas.body.connectMesh(car);

  let stage = assets.stage;
  stage.scene.traverse(child => {
    if (child.isMesh && child.name == "Floor") {
      canvas.floor = child;
      child.material = materials.Floor;
    }
    if (child.name.includes("Area")) {
      const intensity = {
        Area_Fill: 1.8,
        Area_Counter: 2.8,
        Area_Rim: 5,
        Area_Rear: 4
      };
      const pointLight = new THREE.PointLight(0xffffff, intensity[child.name]);
      pointLight.position.copy(child.position);
      pointLight.rotateX(-Math.PI / 2.0);
      scene.add(pointLight);
    }
  });
  scene.add(stage.scene);
};

/* Initialize a snow point mesh.
  3 meshes are currently used for the snow effect. */
const createSnow = (canvas, texture, rotation, size) => {
  var vertices = [];

  for (var i = 0; i < 500; i++) {
    var x = THREE.MathUtils.randFloatSpread(8);
    var y = THREE.MathUtils.randFloatSpread(8);
    var z = THREE.MathUtils.randFloatSpread(8);

    vertices.push(x, y, z);
  }

  var geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));

  var material = new THREE.PointsMaterial({
    color: 0xffffff,
    transparent: true,
    blending: THREE.NormalBlending,
    size: size,
    alphaMap: texture
  });

  material.onBeforeCompile = shader => {
    addNoiseOffset(shader);
    addSnowfall(shader);
    material.userData.shader = shader;
  };
  var points = new THREE.Points(geometry, material);
  points.position.set(0, 4, 0);
  canvas.time.on("tick", dt => {
    if (material.userData.shader) {
      material.userData.shader.uniforms.noiseOffset.value.addScalar(dt % 4);
      material.userData.shader.uniforms.time.value = canvas.time.elapsed / 1000;
    }
    points.rotateY(dt * rotation);
  });

  return points;
};

/* Toggle snow, for winter tyres */
const displaySnow = (canvas, visible) => {
  if (canvas?.snow) {
    canvas.snow.visible = visible;
    canvas.renderFrame = true;
  }
};

/* Toggle PHEV rims */
const setPHEV = (canvas, flag) => {
  canvas.wheels &&
    canvas.wheels.map(wheel => {
      wheel.traverse(child => {
        if (child?.name?.includes("_HEV")) {
          child.visible = !flag;
        } else if (child?.name?.includes("_PHEV")) {
          child.visible = flag;
        }
      });
    });
  canvas.renderFrame = true;
};

/* Queue color transition */
const setColor = (canvas, color) => {
  if (canvas?.colorQueue) {
    canvas.colorQueue.push(color);
  }
};

const setTowbar = (canvas, enable) => {
  canvas.car &&
    canvas.car.traverse(child => {
      if (child.name == "Towbar") {
        child.visible = enable;
      }
    });
  canvas.lastInteractionTime = canvas.time.current;
};

/* Applies a force calibrated to flip the car over from its back */
const flip = canvas => {
  const origin = new THREE.Vector3(0, 0.5, 1);
  const force = new THREE.Vector3(0, canvas.body.mass * 4, 0);

  canvas.lastInteractionTime = canvas.time.current;

  canvas.car.localToWorld(origin);

  canvas.body.applyImpulse(origin, force);
};

/* Detect if the car's roof faces upwards */
const checkForFlip = (canvas, dt) => {
  canvas.carUpDirection.set(0, 1, 0);
  canvas.carUpDirection.applyQuaternion(canvas.car.quaternion);
  canvas.carUpDirection.normalize();

  const dot = canvas.carUpDirection.dot(THREE.Object3D.DefaultUp);

  if (dot < 0.2) {
    canvas.flipTimer += dt;
    if (canvas.flipTimer > 1.5) {
      canvas.flipTimer = 0;
      flip(canvas);
    }
  } else {
    canvas.flipTimer = 0;
  }
};

const respawn = canvas => {
  canvas.body.position.set(0.0, 2.0, 0.0);
  canvas.body.orientation.setFromEuler(0, Math.PI / 4.0, 0);
  canvas.body.linearVelocity.set(0, 0, 0);
  canvas.body.angularVelocity.set(0, 0, 0);
  canvas.wheels.map(wheel => {
    wheel.body.position.copy(wheel.spawnPos);
    wheel.body.setRotation(new THREE.Vector3(0, Math.PI / 4.0, 0));
    wheel.body.linearVelocity.set(0, 0, 0);
    wheel.body.angularVelocity.set(0, 0, 0);
  });
  canvas.body.kinematic = false;

  canvas.lastInteractionTime = canvas.time.current;
};

const update = (dt, canvas) => {
  canvas.world.step();
  canvas.wheels.map(wheel => {
    wheel.body.setQuaternion(canvas.body.orientation);
    wheel.body.angularVelocity.set(0, 0, 0);
    wheel.body.isKinematic = false;

    if (wheel.body.position.y < 0) {
      /* Prevent wheels from bugging into the ground */
      respawn(canvas);
    }
  });

  checkForFlip(canvas, dt);

  /* Wrap position around X axis */
  canvas.carCameraPos.copy(canvas.car.position).project(canvas.camera);
  const edgeLimit = 1.5;
  if (canvas.carCameraPos.x > edgeLimit) {
    const delta = -0.9 * canvas.body.position.x - canvas.body.position.x;
    canvas.body.position.x += delta;
    canvas.wheels.map(wheel => {
      wheel.body.position.add(new THREE.Vector3(delta, 0, 0));
    });
    canvas.lastInteractionTime = canvas.time.current;
  } else if (canvas.carCameraPos.x < -edgeLimit) {
    const delta = -0.9 * canvas.body.position.x - canvas.body.position.x;
    canvas.body.position.x += delta;
    canvas.wheels.map(wheel => {
      wheel.body.position.add(new THREE.Vector3(delta, 0, 0));
    });
    canvas.lastInteractionTime = canvas.time.current;
    /* Give the car a gentle push if it is slightly out of frame*/
  } else if (canvas.carCameraPos.x < -0.7) {
    canvas.pushTimer += dt;
    if (canvas.pushTimer > 2) {
      canvas.pushTimer = 0;
      canvas.body.applyImpulse(canvas.car.position, new THREE.Vector3(Math.sqrt(-canvas.car.position.x) * canvas.body.mass * 3.4, 0, 0));
      canvas.lastInteractionTime = canvas.time.current;
    }
  } else if (canvas.carCameraPos.x > 0.7) {
    canvas.pushTimer += dt;
    if (canvas.pushTimer > 2) {
      canvas.pushTimer = 0;
      canvas.body.applyImpulse(canvas.car.position, new THREE.Vector3(-Math.sqrt(canvas.car.position.x) * canvas.body.mass * 3.4, 0, 0));
      canvas.lastInteractionTime = canvas.time.current;
    }
  } else {
    canvas.pushTimer = 0;
  }

  /* Apply correcting force to keep car centered on the Z axis*/
  if (canvas.body.position.z < -0.5 || canvas.body.position.z > 0.5) {
    const delta = -canvas.body.position.z;
    canvas.centerForce.set(0, 0, delta * canvas.body.mass);
    canvas.body.applyImpulse(canvas.body.position, canvas.centerForce);
    canvas.wheels.map(w => {
      canvas.centerForce.set(0, 0, delta * w.body.mass);
      w.body.applyImpulse(w.body.position, canvas.centerForce);
    });
  }

  if (canvas.time.current - canvas.lastInteractionTime < 6000) {
    canvas.renderFrame = true;
  } else if (canvas.snow.visible) {
    canvas.renderFrame = true;
  } else if (Math.abs(canvas.body.position.x - canvas.oldX) > 0.02) {
    canvas.oldX = canvas.body.position.x;
    canvas.renderFrame = true;
  }
  if (canvas.materials.Body.userData.shader) {
    const uniforms = canvas.materials.Body.userData.shader.uniforms;
    if (canvas.colorQueue.length && uniforms.wipePosition.value == 1.0) {
      const newColor = canvas.colorQueue.shift();
      uniforms.wipeColor.value = new THREE.Color(newColor?.color);
      uniforms.wipeMetalness.value = newColor?.metalness;
      uniforms.wipePosition.value = 0;
    }

    if (uniforms.wipePosition.value < 1) {
      uniforms.wipePosition.value += dt;
      canvas.renderFrame = true;
    } else if (uniforms.wipePosition.value > 1.0) {
      uniforms.wipePosition.value = 1.0;
      canvas.materials.Body.color = uniforms.wipeColor.value;
      canvas.materials.Body.metalness = uniforms.wipeMetalness.value;
    }
  }
};

export const scene = {
  assets: Assets,
  init: init,
  update: update,
  callbacks: {
    setCarColor: (canvas, color) => {
      setColor(canvas, color);
    },
    setBlueColor: canvas => {
      setColor(canvas, canvas.sceneData.colors.blue);
    },
    setBlackColor: canvas => {
      setColor(canvas, canvas.sceneData.colors.black);
    },
    setTowbar: setTowbar,
    displaySnow: displaySnow,
    setPHEV: setPHEV,
    respawn: respawn
  }
};

export const canvas = new Canvas();
